import { isEmpty } from '@aws-amplify/core'
import CircularProgress from '@material-ui/core/CircularProgress'
import Hidden from '@material-ui/core/Hidden'
import { makeStyles } from '@material-ui/core/styles'
import { find } from 'lodash'
import get from 'lodash/get'
import React, {
    useRef,
    useEffect,
    useState,
    useCallback,
    Fragment,
} from 'react'
import {
    useParams,
    useHistory,
    useLocation,
} from 'react-router-dom'
import 'react-table/react-table.css'
import {
    DEFAULT_LOCATION,
    APP_BAR_HEIGHT,
    EQUIPMENT_LIST_PROPERTY_WIDTH,
} from '../../../Constants'
import DisplayError from '../../../fhg/components/DisplayError'
import Grid from '../../../fhg/components/Grid'
import useLazyRequestForServer from '../../../Utils/useLazyRequestForServer'
import '../dashboard/EvalDashboard.scss'
import AssetPropertiesDrawer from '../detail/AssetPropertiesDrawer/AssetPropertiesDrawer2'
import EquipmentDetailMain from '../detail/EquipmentDetailMain'
import EquipmentList from './EquipmentList'
import EquipmentListHeader from './EquipmentListHeader'
import NewItemDialog from './NewItemDialog'
import eventCapture from '../../../eventList'
import { posthogCapture } from '../../../PostHogFunction'
import { Auth } from 'aws-amplify'
import {
    buildRequestHeaders,
    isAdmin,
    isMultipleOrganization,
} from '../../../Utils/ServerUtil'
import {
    useEvaluationIdStore,
    useEvaluationStore,
} from '../../zustandState/ZustandStore'
import { getCurrentOrganizationUrlParam } from '../../../Utils/helpers'
import config from '../../../config/runtime'
import { useItemById } from '../../../services/api'

const useStyles = makeStyles((theme) => ({
    list: { height: '100%', width: '100%' },
    progress: { position: 'fixed', top: '50%', left: '50%', zIndex: 1001 },
    root: {
        height: `calc(100% - ${APP_BAR_HEIGHT + theme.spacing(2)}px) !important`,
        [theme.breakpoints.down('xs')]: { height: '100% !important' },
    },
    rootNoAppBar: { height: `100% !important` },
    inner: {
        flex: '1 1',
        maxWidth: '100%',
        overflow: 'auto',
        backgroundColor: theme.palette.environment.light.level2.base,
        '@media print': { display: 'block' },
    },
}))

/**
 * The component to show the equipment list. Handled requesting information from server so it only needs to be requested
 * once.
 */
export default function EquipmentListMain({ viewMode, routeOptions }) {
    const classes = useStyles();
    // Always call hooks at the top level - never conditionally
    const location = useLocation();
    const history = useHistory();
    const params = useParams();
    
    // Extract id and owner from params
    const { id, owner } = params; // 'id' refers to item_id, 'owner' refers to evaluation owner
    let catalogMatch = viewMode === 'catalog' || viewMode === 'new';

    const tableRef = useRef()

    const evaluationId = useEvaluationIdStore((state) => state.evaluationId)
    const { setCachedEvaluation, cachedEvaluation } = useEvaluationStore()

    const [evaluation, setEvaluation] = useState(null)
    const [evalItem, setEvalItem] = useState(null)

    console.log({ currentEvalItemState: evalItem })

    const [isDrawerOpen, setIsDrawerOpen] = useState(false)

    const [
        fetchCatalog,
        { error: errorCatalog, loading: loadingCatalog, data: dataCatalog },
    ] = useLazyRequestForServer()
    // Using React Query hook for fetching individual items
    const itemQuery = useItemById(id, { enabled: !!id })
    const {
        data: dataAsset,
        error: errorAsset,
        isLoading: isEvalItemLoading,
        refetch: refetchAsset,
    } = itemQuery

    const [isSaving, setIsSaving] = useState(false)
    const [savedTime, setSavedTime] = useState()
    const [isEstimateError, setIsEstimateError] = useState(false)
    const [evalID, setEvalID] = useState('')

    const isNew = get(location, 'state.isNew')
    const isZoomed = get(location, 'state.isZoomed')

    /**
     * Track the notification events for posthog.
     */
    useEffect(() => {
        if (location.search === '?from=notification') {
            posthogCapture(eventCapture.NOTIFICATION_OPEN)
            const newLocation = { ...location }
            newLocation.search = undefined
            history.replace(newLocation)
        }
    }, [location.search, history])

    /**
     * Initialize evalID from evaluationIdStore or cachedEvaluation - ONLY ONCE
     */
    useEffect(() => {
        const newEvalID = evaluationId || cachedEvaluation?.evaluation?.eval_id
        if (newEvalID && newEvalID !== evalID) {
            setEvalID(newEvalID)
        }
    }, [evaluationId, cachedEvaluation?.evaluation?.eval_id]) // Removed evalID from dependencies to prevent loops

    /**
     * Memoize newEvalData to prevent recreation on every render
     */
    const newEvalData = useCallback(async () => {
        if (!evalID) return

        let jwtToken
        try {
            const session = await Auth.currentSession()
            jwtToken = session.idToken.jwtToken
        } catch (e) {
            console.warn('Error refreshing token:', e)
        }

        const headers = buildRequestHeaders(jwtToken)
        const options = { method: 'GET', headers }
        const url = `${config.API_ENDPOINT}/evaluations/${evalID}`

        try {
            const response = await fetch(url, options)
            if (response.ok) {
                const itemData = await response.json()
                console.log(
                    '--------------Fresh Eval Data---------------',
                    itemData
                )
                setEvaluation(itemData)
            } else {
                console.error('Request failed with status:', response.status)
            }
        } catch (error) {
            console.error('Error fetching new evaluation data:', error)
        }
    }, [evalID])

    /**
     * Fetch evaluation data when evalID changes - ONLY ONCE per evalID
     */
    useEffect(() => {
        if (evalID && !evaluation) {
            // Only fetch if we don't have evaluation data
            newEvalData()
        }
    }, [evalID, newEvalData, evaluation]) // Include newEvalData and evaluation check

    /**
     * If the uri owner/uuid changed, fetch the catalog.
     * This effect is primarily for loading the main evaluation list.
     */
    useEffect(() => {
        if (owner && catalogMatch) {
            // Construct the evaluation URI from the owner parameter
            const newEvaluationUri = `/evaluations/${owner}`;
            const existingEvaluationUri = evaluation?.uri?.replace('%7C', '');

            // console.log('Owner', owner);
            // console.log('ExistingURI', existingEvaluationUri);
            // console.log('NewURL', newEvaluationUri);

            // Only fetch catalog if the URI has genuinely changed
            if (
                newEvaluationUri &&
                newEvaluationUri !== existingEvaluationUri
            ) {
                // console.log('Fetching catalog due to owner/URI change:', newEvaluationUri);
                fetchCatalog(newEvaluationUri)
            }
        }
    }, [owner, catalogMatch, evaluation?.uri, fetchCatalog]);

    /**
     * When the catalog data is returned from the server, set the evaluation.
     */
    useEffect(() => {
        if (!isEmpty(dataCatalog)) {
            const evaluationData = Array.isArray(dataCatalog)
                ? dataCatalog[0]
                : dataCatalog
            setEvaluation(evaluationData)
            console.log('Evaluation data set from dataCatalog:', evaluationData)
        }
    }, [dataCatalog])

    /**
     * When the asset data is returned from `fetchAsset`, set the evalItem.
     */
    useEffect(() => {
        if (!isEmpty(dataAsset)) {
            const asset = Array.isArray(dataAsset) ? dataAsset[0] : dataAsset
            // console.log({ settingEvalItem_from_dataAsset_fetch: asset })
            setEvalItem(asset)

            const evaluationUriFromAsset = get(asset, 'open_evaluations[0].uri')
            // Check if we need to load a new evaluation based on the asset's URI.
            if (
                evaluationUriFromAsset &&
                (!evaluation || evaluation.uri !== evaluationUriFromAsset)
            ) {
                console.log(
                    'Asset has evaluation URI, loading evaluation:',
                    evaluationUriFromAsset
                )
                fetchCatalog(evaluationUriFromAsset)
            } else if (!evaluationUriFromAsset) {
                // console.log(
                //     `The fetched asset (${id}) has no open evaluations.`
                // )
            }
        }
    }, [dataAsset, evaluation?.uri, id, fetchCatalog])

    /**
     * Manages the evalItem state based on the 'id' from the URL.
     * This is the primary effect for setting 'evalItem'.
     */
    useEffect(() => {
        if (!id) {
            setEvalItem(null)
            console.log('No ID in URL. Clearing evalItem.')
            return
        }

        // When 'id' changes, immediately clear evalItem to prevent displaying stale data
        setEvalItem(null)
        console.log(`ID changed to ${id}. Clearing evalItem.`)

        let foundAsset = false
        // First, try to find the item in the currently loaded 'evaluation' data.
        if (evaluation && Array.isArray(evaluation.items)) {
            const evalItemFoundInEvaluation = evaluation.items.find(
                (item) =>
                    get(item, 'item.item_id') === id ||
                    get(item, 'item_id') === id
            )

            if (evalItemFoundInEvaluation) {
                foundAsset = true
                console.log({
                    settingEvalItem_from_evaluation_lookup:
                        evalItemFoundInEvaluation,
                })
                setEvalItem(evalItemFoundInEvaluation)
            }
        }

        // Note: useItemById hook will automatically fetch the item when id changes
        // No manual fetch needed here anymore
        if (!foundAsset && dataAsset) {
            console.log(`Setting item from useItemById hook: ${id}`)
            setEvalItem(dataAsset)
        }
    }, [id, evaluation?.items, evaluation, dataAsset]) // Updated dependencies

    /**
     * Memoize loadEvaluation to prevent recreation
     */
    const loadEvaluation = useCallback(
        (uri) => {
            console.log('Loading evaluation from URI:', uri)
            return fetchCatalog(uri)
        },
        [fetchCatalog]
    )

    /**
     * When the data has changed for the item, force a refresh.
     * Memoized to prevent recreation on every render
     */
    const onRefresh = useCallback(
        (item, isRefreshList) => {
            if (item && item.item_id && id === item.item_id) {
                console.log(`Refreshing current item: ${id}`)
                return refetchAsset()
            } else if (isRefreshList) {
                const uri =
                    evaluation?.uri || get(item, 'open_evaluations[0].uri')
                if (uri) {
                    console.log('Refreshing evaluation list from URI:', uri)
                    return fetchCatalog(uri)
                } else {
                    console.warn(
                        'Cannot refresh list: no evaluation URI available.'
                    )
                    return Promise.resolve()
                }
            } else {
                return Promise.resolve()
            }
        },
        [id, evaluation?.uri, refetchAsset, fetchCatalog]
    )

    /**
     * Indicate the saving and saved time for estimates.
     */
    const handleSaving = useCallback((isSaving, savedTime) => {
        setIsSaving(isSaving)

        if (isSaving) {
            setTimeout(() => {
                setIsSaving(false) // Auto-clear saving state after a timeout
            }, 6000)
        }

        setSavedTime(savedTime)
    }, [])

    /**
     * When the user closes the sidebar, mark the sidebar closed.
     */
    const onDrawerClose = useCallback(() => {
        setIsDrawerOpen(false)
    }, [])

    /**
     * When the user opens the sidebar, mark the sidebar open.
     */
    const onDrawerOpen = useCallback(() => {
        setIsDrawerOpen(true)
    }, [])

    /**
     * When the detail is being refreshed, update the isSaving and isError.
     */
    const onDetailRefresh = useCallback(
        (isSaving, isError) => {
            setIsSaving(isSaving)
            setSavedTime(new Date())
            setIsEstimateError(isError)

            // If saving is complete and no error, trigger a full list refresh.
            if (!isSaving && !isError) {
                onRefresh(undefined, true)
            }
        },
        [onRefresh]
    )

    return (
        <Fragment>
            {(errorAsset || errorCatalog) && (
                <DisplayError
                    error={(() => {
                        if (errorCatalog?.response?.status === 404) {
                            errorCatalog.message =
                                'You may not have access to this catalog. If you believe you should, please contact the administrator for assistance.'
                        }
                        if (errorAsset?.response?.status === 404) {
                            errorAsset.message =
                                'You may not have access to this asset. If you believe you should, please contact the administrator for assistance.'
                        }
                        return errorAsset || errorCatalog
                    })()}
                />
            )}

            <Hidden xsDown>
                {evaluation && ( // Only render header if evaluation data is available
                    <EquipmentListHeader
                        evaluation={evaluation}
                        cachedEvaluation={cachedEvaluation}
                        allowExport={!isNew}
                        saving={isSaving}
                        savedTime={savedTime}
                        onDrawerOpen={onDrawerOpen}
                        isDrawerOpen={isDrawerOpen}
                        smBreakpoint={false}
                        tableRef={tableRef}
                        evalItem={evalItem} // Pass evalItem to header if needed
                    />
                )}
            </Hidden>
            <Grid
                container
                className={isZoomed ? classes.rootNoAppBar : classes.root}
                name={'EquipmentListMain-root'}
                direction={'column'}
                wrap={'nowrap'}
                spacing={0}
            >
                {((isEvalItemLoading && id) || (loadingCatalog && !dataCatalog)) && ( // Only show loading when actually needed
                    <CircularProgress className={classes.progress} />
                )}

                <Grid
                    container
                    className={classes.inner}
                    name={'EquipmentListMain-inner'}
                    direction={'row'}
                    wrap={'nowrap'}
                    spacing={0}
                >
                    {(() => {
                        // Determine what to render based on viewMode and location state
                        if (viewMode === 'new' || get(location, 'state.isNew') === true) {
                            return (
                                <NewItemDialog
                                    key="NewItemDialog-New"
                                    evaluation={evaluation}
                                    onUpdate={onRefresh}
                                />
                            );
                        } else if (viewMode === 'catalog') {
                            return (
                                <EquipmentList
                                    tableRef={tableRef}
                                    evaluation={evaluation}
                                    onRefresh={loadEvaluation} // loadEvaluation now just fetches catalog
                                    isEstimateSaving={isSaving}
                                    isEstimateError={isEstimateError}
                                    isLoading={loadingCatalog}
                                    onSaving={handleSaving}
                                />
                            );
                        } else if (viewMode === 'edit') {
                            if (get(location, 'state.isEdit') === true) {
                                return (
                                    <NewItemDialog
                                        evalItem={evalItem}
                                        evaluation={evaluation}
                                        onUpdate={onRefresh}
                                    />
                                );
                            } else {
                                return (
                                    <>
                                        <EquipmentDetailMain
                                            evalItem={evalItem} // evalItem will be null/object based on current ID
                                            onRefresh={onRefresh}
                                            evaluation={evaluation}
                                            isLoading={isEvalItemLoading}
                                            isDrawerOpen={isDrawerOpen}
                                            onDrawerClose={onDrawerClose}
                                            onDrawerOpen={onDrawerOpen}
                                        />
                                        <AssetPropertiesDrawer
                                            evalItem={evalItem}
                                            evaluation={evaluation}
                                            breakpoint={'sm'}
                                            width={
                                                EQUIPMENT_LIST_PROPERTY_WIDTH
                                            }
                                            showEquipmentTitle={false}
                                            onSaving={onDetailRefresh}
                                            isOpen={isDrawerOpen && evalItem !== null}
                                            onClose={onDrawerClose}
                                        />
                                    </>
                                );
                            }
                        }
                        return null;
                    })()}
                </Grid>
            </Grid>
        </Fragment>
    )
}
